const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const eur = (n) => "€ " + (Number(n) || 0).toFixed(2);
const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

// Opens a clean, branded, printable version of a quote in a new window and
// triggers the browser print dialog (user can print or "Save as PDF").
// Returns false if the popup was blocked.
export function printQuote(q) {
  const origin = window.location.origin;
  const righe = q.righe || [];
  let subtotale = 0;
  const rowsHtml = righe.map((r) => {
    const qty = Number(r.quantita) || 0;
    const price = Number(r.prezzo_unitario) || 0;
    let line = qty * price;
    line -= line * ((Number(r.sconto_perc) || 0) / 100);
    subtotale += line;
    return `<tr>
      <td class="desc">${esc(r.descrizione)}${r.sconto_perc > 0 ? ` <span class="disc">−${r.sconto_perc}%</span>` : ""}</td>
      <td class="c">${qty}</td>
      <td class="r">${eur(price)}</td>
      <td class="r">${eur(line)}</td>
    </tr>`;
  }).join("");
  if (q.subtotale != null) subtotale = Number(q.subtotale);
  const scontoPerc = Number(q.sconto_globale_perc) || 0;
  const scontoVal = (q.sconto_totale != null) ? Number(q.sconto_totale) : subtotale * (scontoPerc / 100);
  const totale = (q.totale != null) ? Number(q.totale) : subtotale - scontoVal;
  const pagamento = [q.modalita_pagamento, q.metodo_pagamento].filter(Boolean).join(" · ");

  const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8">
  <title>${esc(q.numero || "Preventivo")} — PT-MED</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; margin: 0; padding: 32px; }
    .head { display:flex; align-items:center; gap:14px; border-bottom:3px solid #17B8B0; padding-bottom:16px; margin-bottom:24px; }
    .head img { height:56px; width:56px; border-radius:12px; object-fit:cover; }
    .brand h1 { margin:0; font-size:20px; color:#16212B; }
    .brand { flex: 1; }
    .brand p { margin:2px 0 0; font-size:11px; color:#64748b; line-height:1.4; }
    .meta { margin-left:auto; text-align:right; font-size:12px; color:#64748b; }
    .num { font-size:22px; font-weight:bold; color:#17B8B0; letter-spacing:1px; }
    .for { margin:0 0 18px; }
    .for .lbl { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#94a3b8; }
    .for .name { font-size:15px; font-weight:bold; }
    table.items { width:100%; border-collapse:collapse; font-size:13px; border:1px solid #e2e8f0; }
    table.items th { background:#f8fafc; text-align:left; padding:8px 12px; font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:#64748b; }
    table.items td { padding:10px 12px; border-top:1px solid #f1f5f9; }
    td.c, th.c { text-align:center; } td.r, th.r { text-align:right; }
    .disc { color:#dc2626; font-size:11px; }
    .totals { margin-top:16px; margin-left:auto; width:260px; font-size:13px; }
    .totals div { display:flex; justify-content:space-between; padding:3px 0; }
    .totals .tot { border-top:2px solid #e2e8f0; margin-top:4px; padding-top:8px; font-size:16px; font-weight:bold; }
    .totals .tot span.v { color:#17B8B0; }
    .pay { margin-top:18px; font-size:12px; color:#475569; }
    .note { margin-top:14px; background:#f8fafc; border-radius:8px; padding:12px; font-size:12px; white-space:pre-line; color:#475569; }
    .cond { margin-top:24px; border-top:1px solid #e2e8f0; padding-top:16px; }
    .cond h3 { font-size:12px; text-transform:uppercase; letter-spacing:1px; color:#64748b; margin:0 0 8px; }
    .cond p { font-size:11px; line-height:1.6; color:#475569; white-space:pre-line; margin:0; }
    .foot { margin-top:28px; font-size:10.5px; color:#64748b; border-top:1px solid #e2e8f0; padding-top:12px; line-height:1.6; }
    .foot div { }
    @page { margin: 16mm; }
    @media print { body { padding:0; } }
  </style></head><body>
    <div class="head">
      <img src="${origin}/ptmed-logo-dark.jpg" alt="PT-MED" />
      <div class="brand">
        <h1>PT-MED</h1>
        <p>Via Leone Pancaldo 60, 37138 Verona (VR) · Italia</p>
        <p>Tel. 342 1439782 · info@ptmed.it · www.ptmed.it</p>
      </div>
      <div class="meta">
        <div class="num">${esc(q.numero || "")}</div>
        <div>Data: ${fmtDate(q.created_at)}</div>
        ${q.validita ? `<div>Valido fino al: ${fmtDate(q.validita)}</div>` : ""}
      </div>
    </div>
    <div class="for"><div class="lbl">Preparato per</div><div class="name">${esc(q.cliente_nome || "")}</div></div>
    <table class="items">
      <thead><tr><th>Descrizione</th><th class="c">Qtà</th><th class="r">Prezzo</th><th class="r">Totale</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="totals">
      <div><span>Subtotale</span><span>${eur(subtotale)}</span></div>
      ${scontoVal > 0 ? `<div style="color:#dc2626"><span>Sconto ${scontoPerc ? scontoPerc + "%" : ""}</span><span>− ${eur(scontoVal)}</span></div>` : ""}
      <div class="tot"><span>Totale</span><span class="v">${eur(totale)}</span></div>
    </div>
    ${pagamento ? `<div class="pay"><strong>Pagamento:</strong> ${esc(pagamento)}</div>` : ""}
    ${q.note ? `<div class="note">${esc(q.note)}</div>` : ""}
    ${q.condizioni ? `<div class="cond"><h3>Condizioni</h3><p>${esc(q.condizioni)}</p></div>` : ""}
    <div class="foot">
      <div><strong>PT-MED</strong> — Alberto Piovan</div>
      <div>Codice Fiscale: PVNLRT86P09L781L · P.IVA: 04489320236</div>
      <div>Coordinate bancarie: intestatario Alberto Piovan · Banca Revolut</div>
      <div>IBAN IT19P0366901600587876224006 · BIC/SWIFT REVOITM2</div>
    </div>
  </body></html>`;

  const w = window.open("", "_blank", "width=840,height=1000");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  const trigger = () => { try { w.print(); } catch (e) { /* noop */ } };
  if (w.document.readyState === "complete") setTimeout(trigger, 400);
  else w.onload = () => setTimeout(trigger, 400);
  return true;
}
